import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import {
    Mail,
    Sparkles,
    Send,
    History,
    Users,
    Building2,
    UserCheck,
    Check,
    Loader2,
    X,
    Edit3,
} from "lucide-react";

const OCCASION_OPTIONS = [
    { value: "internal_memo", label: "Internal memo" },
    { value: "public_holiday", label: "Public holiday" },
    { value: "announcement", label: "Announcement" },
];

const TONE_OPTIONS = [
    "warm-professional",
    "formal",
    "celebratory",
    "concise",
    "empathetic",
];

const AUDIENCE_OPTIONS = [
    { value: "all_active", label: "All active employees", icon: Users },
    { value: "by_department", label: "By department", icon: Building2 },
    { value: "hand_picked", label: "Hand-picked", icon: UserCheck },
];

export default function Memos() {
    const [brief, setBrief] = useState("");
    const [occasion, setOccasion] = useState("internal_memo");
    const [tone, setTone] = useState("warm-professional");
    const [drafting, setDrafting] = useState(false);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [audience, setAudience] = useState("all_active");
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [sending, setSending] = useState(false);
    const [history, setHistory] = useState([]);

    const loadAll = async () => {
        const [deps, emps, hist] = await Promise.all([
            api.get("/departments").catch(() => ({ data: [] })),
            api.get("/employees").catch(() => ({ data: [] })),
            api.get("/memos").catch(() => ({ data: [] })),
        ]);
        setDepartments(deps.data || []);
        setEmployees((emps.data || []).filter((e) => e.status === "active" && e.email));
        setHistory(hist.data || []);
    };

    useEffect(() => { loadAll(); }, []);

    const filteredEmployees = useMemo(() => {
        const q = employeeSearch.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) => employeeMatches(e, q));
    }, [employees, employeeSearch]);

    const recipientCount = useMemo(() => {
        if (audience === "all_active") return employees.length;
        if (audience === "by_department") {
            return employees.filter((e) => selectedDepartments.includes(e.department)).length;
        }
        return selectedEmployeeIds.length;
    }, [audience, employees, selectedDepartments, selectedEmployeeIds]);

    const draft = async () => {
        if (!brief.trim()) { toast.error("Tell the AI what to write about."); return; }
        setDrafting(true);
        try {
            const { data } = await api.post("/memos/draft", {
                brief: brief.trim(),
                occasion,
                tone,
            });
            setSubject(data.subject || "");
            setBody(data.body_text || "");
            toast.success("Draft ready — review and edit before sending.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDrafting(false);
        }
    };

    const send = async () => {
        if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required."); return; }
        if (audience === "by_department" && selectedDepartments.length === 0) {
            toast.error("Select at least one department."); return;
        }
        if (audience === "hand_picked" && selectedEmployeeIds.length === 0) {
            toast.error("Select at least one employee."); return;
        }
        setSending(true);
        try {
            const payload = {
                subject: subject.trim(),
                body_text: body.trim(),
                occasion,
                audience,
                departments: audience === "by_department" ? selectedDepartments : null,
                employee_ids: audience === "hand_picked" ? selectedEmployeeIds : null,
            };
            const { data } = await api.post("/memos/send", payload);
            toast.success(`Sent to ${data.sent?.length || 0} recipient${data.sent?.length === 1 ? "" : "s"}${data.failed?.length ? ` · ${data.failed.length} failed` : ""}.`);
            setBrief(""); setSubject(""); setBody("");
            setSelectedDepartments([]); setSelectedEmployeeIds([]);
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSending(false);
        }
    };

    const toggleDept = (d) => {
        setSelectedDepartments((p) => p.includes(d) ? p.filter((x) => x !== d) : [...p, d]);
    };
    const toggleEmp = (id) => {
        setSelectedEmployeeIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
    };

    return (
        <div className="px-6 md:px-12 py-10 md:py-16" data-testid="memos-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">HR Communications</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.02] flex items-center gap-3">
                        <Mail className="w-8 h-8 sm:w-9 sm:h-9 text-klein" strokeWidth={1.5} /> AI Email Drafts
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">
                        Sketch a brief in plain English. Claude will draft a polished memo you can edit before sending to all staff, specific departments, or hand-picked recipients.
                    </p>
                </div>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-px bg-ink/10 border border-ink/10">
                {/* Left: composer */}
                <div className="lg:col-span-3 bg-white p-5 sm:p-8" data-testid="memo-composer">
                    <div className="overline">Step 1 · Tell the AI what to write</div>
                    <textarea
                        value={brief}
                        onChange={(e) => setBrief(e.target.value)}
                        rows={4}
                        placeholder="e.g. Notify staff that Monday March 11 is a public holiday for Eid al-Fitr. Office reopens Tuesday."
                        data-testid="memo-brief"
                        className="mt-3 w-full border border-ink/15 px-3 py-2 text-sm"
                    />
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="overline">Occasion</label>
                            <select
                                value={occasion}
                                onChange={(e) => setOccasion(e.target.value)}
                                data-testid="memo-occasion"
                                className="mt-1 w-full border border-ink/15 px-3 py-2 text-sm bg-white"
                            >
                                {OCCASION_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="overline">Tone</label>
                            <select
                                value={tone}
                                onChange={(e) => setTone(e.target.value)}
                                data-testid="memo-tone"
                                className="mt-1 w-full border border-ink/15 px-3 py-2 text-sm bg-white"
                            >
                                {TONE_OPTIONS.map((t) => (
                                    <option key={t} value={t}>{t.replace(/-/g, " ")}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <button
                        onClick={draft}
                        disabled={drafting || !brief.trim()}
                        data-testid="memo-draft-btn"
                        className="mt-4 inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90 disabled:opacity-60"
                    >
                        {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {drafting ? "Drafting…" : "Draft with AI"}
                    </button>

                    {(subject || body) && (
                        <div className="mt-8 pt-8 border-t border-ink/10">
                            <div className="overline flex items-center gap-2"><Edit3 className="w-3 h-3" /> Step 2 · Review &amp; edit</div>
                            <label className="block mt-3 text-xs uppercase tracking-[0.12em] text-ink/60 mb-1">Subject</label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                data-testid="memo-subject"
                                className="w-full border border-ink/15 px-3 py-2 text-sm"
                            />
                            <label className="block mt-3 text-xs uppercase tracking-[0.12em] text-ink/60 mb-1">Body</label>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                rows={12}
                                data-testid="memo-body"
                                className="w-full border border-ink/15 px-3 py-2 text-sm font-mono leading-relaxed"
                            />
                        </div>
                    )}
                </div>

                {/* Right: recipients + send */}
                <div className="lg:col-span-2 bg-white p-5 sm:p-8" data-testid="memo-recipients">
                    <div className="overline">Step 3 · Who gets this?</div>
                    <div className="mt-3 space-y-2">
                        {AUDIENCE_OPTIONS.map((a) => {
                            const Icon = a.icon;
                            const active = audience === a.value;
                            return (
                                <button
                                    key={a.value}
                                    onClick={() => setAudience(a.value)}
                                    data-testid={`memo-audience-${a.value}`}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm border transition-colors ${active ? "border-klein bg-klein/5 text-ink" : "border-ink/15 text-ink/70 hover:bg-ink/5"}`}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span className="flex-1 text-left">{a.label}</span>
                                    {active && <Check className="w-4 h-4 text-klein" />}
                                </button>
                            );
                        })}
                    </div>

                    {audience === "by_department" && (
                        <div className="mt-4">
                            <label className="overline">Departments</label>
                            <div className="mt-2 max-h-60 overflow-y-auto border border-ink/10 divide-y divide-ink/5">
                                {departments.length === 0 && (
                                    <div className="px-3 py-2 text-xs text-ink/40">No departments yet.</div>
                                )}
                                {departments.map((d) => (
                                    <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-ink/5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedDepartments.includes(d.name)}
                                            onChange={() => toggleDept(d.name)}
                                            data-testid={`memo-dept-${d.name}`}
                                            className="accent-klein"
                                        />
                                        <span className="flex-1">{d.name}</span>
                                        <span className="text-xs text-ink/40">
                                            {employees.filter((e) => e.department === d.name).length}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {audience === "hand_picked" && (
                        <div className="mt-4">
                            <label className="overline">Pick employees</label>
                            <input
                                type="search"
                                placeholder="Search name, staff no, department, division, phone…"
                                value={employeeSearch}
                                onChange={(e) => setEmployeeSearch(e.target.value)}
                                data-testid="memo-emp-search"
                                className="mt-2 w-full border border-ink/15 px-3 py-2 text-sm"
                            />
                            <div className="mt-2 max-h-60 overflow-y-auto border border-ink/10 divide-y divide-ink/5">
                                {filteredEmployees.length === 0 && (
                                    <div className="px-3 py-2 text-xs text-ink/40">No matches.</div>
                                )}
                                {filteredEmployees.slice(0, 200).map((e) => (
                                    <label key={e.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-ink/5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedEmployeeIds.includes(e.id)}
                                            onChange={() => toggleEmp(e.id)}
                                            data-testid={`memo-emp-${e.id}`}
                                            className="accent-klein"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate">{e.first_name} {e.last_name}</div>
                                            <div className="text-xs text-ink/50 truncate">{e.email} · {e.department || "—"}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 pt-6 border-t border-ink/10">
                        <div className="flex items-center justify-between text-sm">
                            <div className="text-ink/60">Will send to</div>
                            <div className="font-display font-bold text-lg tabular-nums text-ink" data-testid="memo-recipient-count">
                                {recipientCount} recipient{recipientCount === 1 ? "" : "s"}
                            </div>
                        </div>
                        <button
                            onClick={send}
                            disabled={sending || !subject.trim() || !body.trim() || recipientCount === 0}
                            data-testid="memo-send-btn"
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-ink text-white px-5 py-3 text-sm hover:bg-ink/90 disabled:opacity-60"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {sending ? "Sending…" : `Send to ${recipientCount}`}
                        </button>
                    </div>
                </div>
            </div>

            {/* History */}
            <div className="mt-16">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="overline">Archive</div>
                        <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-3">
                            <History className="w-6 h-6 text-ink/70" /> Recently sent memos
                        </h2>
                    </div>
                </div>
                <div className="mt-4 bg-white border border-ink/10" data-testid="memo-history">
                    {history.length === 0 ? (
                        <div className="px-6 py-10 text-center text-sm text-ink/40">
                            No memos sent yet. Compose your first above.
                        </div>
                    ) : (
                        <ul className="divide-y divide-ink/5">
                            {history.map((m) => (
                                <li key={m.id} className="px-4 sm:px-6 py-4 flex items-start justify-between gap-4" data-testid={`memo-row-${m.id}`}>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-ink truncate">{m.subject}</div>
                                        <div className="text-xs text-ink/50 mt-0.5">
                                            {m.audience_summary} · sent {new Date(m.sent_at).toLocaleString()} by {m.sender_email}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-sm font-display font-bold tabular-nums">{m.recipients_count}</div>
                                        <div className="text-[10px] uppercase tracking-[0.12em] text-ink/50">delivered</div>
                                        {m.failed_count > 0 && (
                                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-red-600">
                                                <X className="w-3 h-3" /> {m.failed_count} failed
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
